import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { reelsApi } from "../api/reels.api";
import { reelsKeys } from "../constants/query-keys";

/** Same size as the Reels page asks for, so both share one cached list. */
const FETCH_LIMIT = 100;

/**
 * The published reel for one venue, as the app finds it
 * (apps/mobile/src/features/reels/api/use-activity-video.ts): the activity
 * endpoints carry no video, so the venue's reel is picked out of GET /reels
 * by activitySlug. Not a suspense query: the page renders its photos first
 * and the "Watch video" pill appears once the list arrives, so server and
 * first client render agree (no video either way).
 */
export function useActivityVideo(slug: string): { url: string; poster?: string } | null {
  const { i18n } = useTranslation();
  const { data } = useQuery({
    queryKey: reelsKeys.list({ limit: FETCH_LIMIT, lang: i18n.language }),
    queryFn: () => reelsApi.list({ limit: FETCH_LIMIT, lang: i18n.language }),
    staleTime: 5 * 60 * 1000,
  });
  const reel = (data?.data ?? []).find((r) => r.activitySlug === slug && !!r.videoUrl);
  if (!reel) return null;
  // With no poster row the API falls back to a gallery photo, or, failing
  // that, to the video itself; a video is no use as a <video> poster.
  const thumb = reel.thumbnail ?? undefined;
  return { url: reel.videoUrl, poster: thumb && !VIDEO_FILE.test(thumb) ? thumb : undefined };
}

const VIDEO_FILE = /\.(mp4|mov|webm|m4v)(\?|#|$)/i;
